<template>
  <main class="dashboard" dir="rtl">
    <header class="dash-head">
      <div class="head-copy">
        <span class="vn-kicker"><b>KPI</b> وضعیت میدان در یک نگاه</span>
        <h1>{{ rangeLabel }}</h1>
        <p v-if="updatedAt">به‌روزرسانی {{ updatedAt }}</p>
      </div>
      <div class="head-actions">
        <div v-if="iframeLink" class="segmented on-dark" role="tablist" aria-label="نمای داشبورد">
          <button type="button" role="tab" :aria-selected="view === 'kpi' ? 'true' : 'false'" :class="{ active: view === 'kpi' }" @click="view = 'kpi'">شاخص‌ها</button>
          <button type="button" role="tab" :aria-selected="view === 'embed' ? 'true' : 'false'" :class="{ active: view === 'embed' }" @click="view = 'embed'">داشبورد تحلیلی</button>
        </div>
        <button v-if="view === 'kpi'" type="button" class="ghost-btn" :disabled="loading" @click="load"><VnIcon name="forecast" />{{ loading ? 'در حال دریافت…' : 'به‌روزرسانی' }}</button>
      </div>
      <span class="vn-bars head-bars" aria-hidden="true" />
    </header>

    <section v-if="view === 'embed'" class="embed-frame">
      <iframe :src="iframeLink" title="داشبورد تحلیلی" frameborder="0" allowfullscreen></iframe>
    </section>

    <section v-else-if="denied" class="panel state-panel" role="note">
      <EmptyState kind="generic" title="داشبورد مدیریتی برای نقش شما فعال نیست" description="این نما برای نقش‌هایی است که کل پروژه را مدیریت می‌کنند. از منوی کناری به بخش‌های خود بروید." />
    </section>

    <template v-else>
      <section class="filters" aria-label="بازه داشبورد">
        <div class="segmented" role="group" aria-label="بازه زمانی">
          <button v-for="item in presets" :key="item.key" type="button" :class="{ active: preset === item.key }" :aria-pressed="preset === item.key ? 'true' : 'false'" @click="setPreset(item.key)">{{ item.label }}</button>
          <button type="button" :class="{ active: preset === 'custom' }" :aria-pressed="preset === 'custom' ? 'true' : 'false'" @click="openCustom">بازه دلخواه</button>
          <input id="dashboard-range" class="range-anchor" readonly tabindex="-1" aria-hidden="true" />
          <date-picker v-model="customRange" range format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" custom-input="#dashboard-range"
                       :max="today" :show="customOpen" @close="customOpen = false" @change="applyCustom" />
        </div>
        <button v-if="previousRange" type="button" class="chip" @click="restoreRange">بازگشت به بازه قبلی<span aria-hidden="true">×</span></button>
      </section>

      <div v-if="error" class="error" role="alert">{{ error }} <button type="button" @click="load">تلاش دوباره</button></div>

      <div class="dash-body" :class="{ refreshing: loading && loaded }">
        <div v-if="!loaded && loading" class="kpis" role="status" aria-label="در حال دریافت داده‌ها">
          <span v-for="n in 8" :key="n" class="skeleton" />
        </div>
        <template v-if="loaded">
          <section class="kpis" aria-label="شاخص‌های کلیدی">
            <KpiTile label="ویزیت برنامه‌ریزی‌شده" :value="fa(kpi.created)" :hint="deltaHint(kpi.created, previous.created)" :hint-tone="deltaTone(kpi.created, previous.created)" icon="calendar" to="/visitmanagment/lists" />
            <KpiTile label="ویزیت انجام‌شده" :value="fa(kpi.done)" :hint="deltaHint(kpi.done, previous.done)" :hint-tone="deltaTone(kpi.done, previous.done)" tone="teal" icon="audit" to="/visitmanagment/acceptedvisits" />
            <KpiTile label="منتظر بررسی گزارش" :value="fa(kpi.awaiting_review)" :hint="kpi.awaiting_review ? 'گزارش‌هایی که هنوز تأیید یا رد نشده‌اند' : 'گزارش بررسی‌نشده‌ای نیست'" :hint-tone="kpi.awaiting_review ? 'bad' : 'muted'" tone="amber" icon="survey" to="/visitmanagment/completeVisited" />
            <KpiTile label="نرخ تأیید گزارش" :value="percent(kpi.approval_rate)" :hint="`${fa(kpi.approved)} تأیید · ${fa(kpi.rejected)} رد`" :tone="kpi.approval_rate !== null && kpi.approval_rate < 80 ? 'pink' : 'teal'" icon="shield" />
            <KpiTile label="ویزیت‌های باز" :value="fa(kpi.open)" hint="انجام‌نشده یا در حال انجام، فارغ از بازه" icon="store" to="/visitmanagment/notcompeletevisits" />
            <KpiTile label="دیرکرد" :value="fa(kpi.overdue)" :hint="kpi.open ? `${percent(kpi.overdue * 100 / kpi.open)} از ویزیت‌های باز` : 'ویزیت بازی نیست'" :hint-tone="kpi.overdue ? 'bad' : 'muted'" tone="pink" icon="alarm" />
            <KpiTile label="عکس ثبت‌شده" :value="fa(kpi.photos)" :hint="`${fa(kpi.answers)} پاسخ به پرسش‌ها`" tone="teal" icon="camera" />
            <KpiTile label="پرسشنامه‌های پرشده" :value="fa(kpi.surveys)" :hint="`${fa(kpi.surveys_done)} مورد تکمیل‌شده`" tone="amber" icon="census" to="/suveymanagment/completesurvey" />
          </section>

          <div class="grid two-one">
            <section class="panel" aria-labelledby="trend-title">
              <div class="panel-head"><div><h2 id="trend-title">روند ویزیت‌ها</h2><p>{{ trend.step === 1 ? 'روزانه' : 'هفتگی' }}؛ روی یک {{ trend.step === 1 ? 'روز' : 'هفته' }} کلیک کنید تا داشبورد به آن محدود شود.</p></div></div>
              <TrendChart :points="trend.points" :step="trend.step" :series="trendSeries" @select="pickPoint" />
            </section>
            <section class="panel" aria-labelledby="status-title">
              <div class="panel-head"><div><h2 id="status-title">وضعیت ویزیت‌ها</h2><p>کارهای انجام‌شده در بازه و کارهای باز؛ برای دیدن فهرست کلیک کنید.</p></div></div>
              <BarList :items="statusRows" />
            </section>
          </div>

          <div class="grid halves">
            <section class="panel" aria-labelledby="staff-title">
              <div class="panel-head"><div><h2 id="staff-title">کارکرد کارشناسان میدانی</h2><p>ده نفر پرکارتر در بازه، به‌همراه ویزیت‌های بازشان.</p></div></div>
              <StaffBars :rows="staff" />
            </section>
            <section class="panel" aria-labelledby="city-title">
              <div class="panel-head"><div><h2 id="city-title">شهرها</h2><p>ویزیت‌های انجام‌شده در بازه به تفکیک شهر.</p></div></div>
              <BarList :items="named(data.cities)" color="var(--teal-500)" />
            </section>
          </div>

          <div class="grid thirds">
            <section class="panel" aria-labelledby="types-title">
              <div class="panel-head"><div><h2 id="types-title">نوع ویزیت</h2><p>ویزیت‌های برنامه‌ریزی‌شده در بازه.</p></div></div>
              <BarList :items="named(data.types)" stacked />
            </section>
            <section class="panel" aria-labelledby="category-title">
              <div class="panel-head"><div><h2 id="category-title">دسته فروشگاه</h2><p>ویزیت‌های انجام‌شده در بازه.</p></div></div>
              <BarList :items="named(data.categories)" color="var(--amber-500)" />
            </section>
            <section class="panel" aria-labelledby="service-title">
              <div class="panel-head"><div><h2 id="service-title">پشتیبانی و رضایت</h2><p>وضعیت فعلی پروژه.</p></div></div>
              <div class="service-grid">
                <router-link to="/ticket/ticketlist" class="service-tile"><span>تیکت در انتظار پاسخ</span><b class="vn-num" :class="{ alert: kpi.tickets_waiting }">{{ fa(kpi.tickets_waiting) }}</b><small>نیازمند پاسخ پشتیبانی</small></router-link>
                <div class="service-tile"><span>میانگین امتیاز ویزیت‌ها</span><b class="vn-num">{{ kpi.average_rate === null ? '—' : fa(kpi.average_rate, 1) }}</b><small>{{ kpi.average_rate === null ? 'امتیازی در این بازه ثبت نشده' : 'امتیازهای ثبت‌شده در بازه' }}</small></div>
              </div>
            </section>
          </div>
        </template>
      </div>
    </template>
  </main>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import EmptyState from '@/components/EmptyState/index.vue';
import KpiTile from './components/KpiTile.vue';
import BarList from './components/BarList.vue';
import TrendChart from './components/TrendChart.vue';
import StaffBars from './components/StaffBars.vue';
import { fa, jDate, percent } from './components/format';

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const PRESETS = ['7', '30', '90', '365'];
const STATUSES = [
  { key: '0', label: 'انجام‌نشده', color: 'var(--navy-300)', to: '/visitmanagment/lists' },
  { key: '1', label: 'در حال انجام', color: 'var(--amber-500)', to: '/visitmanagment/notcompeletevisits' },
  { key: '2', label: 'منتظر بررسی', color: 'var(--navy-700)', to: '/visitmanagment/completeVisited' },
  { key: '3', label: 'تأییدشده', color: 'var(--teal-500)', to: '/visitmanagment/acceptedvisits' },
  { key: '4', label: 'ردشده', color: 'var(--pink-500)', to: '/visitmanagment/lists' },
];

// The calendar day in Tehran, as the backend counts days.
function localToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tehran', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function shift(iso, days) {
  const date = new Date(iso + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'Dashboard',
  components: { VnIcon, EmptyState, KpiTile, BarList, TrendChart, StaffBars },
  data: () => ({
    view: 'kpi', iframeLink: null,
    preset: '30', range: null, previousRange: null, today: localToday(), customRange: [], customOpen: false,
    data: null, loading: false, loaded: false, error: '', denied: false, updatedAt: '', sequence: 0,
    presets: [{ key: '7', label: '۷ روز' }, { key: '30', label: '۳۰ روز' }, { key: '90', label: '۹۰ روز' }, { key: '365', label: 'یک سال' }],
    trendSeries: [
      { key: 'created', label: 'برنامه‌ریزی‌شده', color: 'var(--navy-700)' },
      { key: 'done', label: 'انجام‌شده', color: 'var(--amber-500)' },
      { key: 'approved', label: 'تأییدشده', color: 'var(--teal-500)' },
    ],
  }),
  computed: {
    project() { return this.$STORE.state.userConfig.setProjectId; },
    kpi() { return this.data ? this.data.kpis : {}; },
    previous() { return this.data ? this.data.previous : {}; },
    rangeLabel() {
      if (!this.range) return 'داشبورد';
      if (this.range.from === this.range.to) return jDate(this.range.from, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      return `${jDate(this.range.from, { year: 'numeric', month: 'long', day: 'numeric' })} تا ${jDate(this.range.to, { year: 'numeric', month: 'long', day: 'numeric' })}`;
    },
    // Long ranges read better week by week; weeks are counted from the start of the range.
    trend() {
      const days = (this.data && this.data.series) || [];
      if (days.length <= 62) return { step: 1, points: days.map(day => ({ ...day, from: day.date, to: day.date })) };
      const points = [];
      for (let i = 0; i < days.length; i += 7) {
        const week = days.slice(i, i + 7);
        points.push(week.reduce((sum, day) => ({ ...sum, created: sum.created + day.created, done: sum.done + day.done, approved: sum.approved + day.approved }),
          { from: week[0].date, to: week[week.length - 1].date, created: 0, done: 0, approved: 0 }));
      }
      return { step: 7, points };
    },
    statusRows() { return this.data ? STATUSES.map(item => ({ ...item, value: this.data.statuses[item.key] || 0 })) : []; },
    staff() {
      return this.data ? this.data.staff.map(row => ({ ...row, waiting: Math.max(0, row.done - row.approved - row.rejected) })) : [];
    },
  },
  watch: { project() { this.load(); this.loadEmbed(); } },
  created() { this.readQuery(); },
  mounted() { this.load(); this.loadEmbed(); },
  methods: {
    fa, percent,
    named(rows) { return (rows || []).map((row, index) => ({ key: row.id || `${index}-${row.name}`, label: row.name, value: row.value })); },
    deltaHint(current, before) {
      if (!before) return current ? 'در بازه‌ی قبل موردی نبود' : '';
      if (current >= before * 3) return `${fa(current / before, 1)} برابر بازه‌ی قبل`;
      const change = Math.round(((current - before) / before) * 100);
      if (!change) return 'هم‌اندازه‌ی بازه‌ی قبل';
      return `${fa(Math.abs(change))}٪ ${change > 0 ? 'بیشتر' : 'کمتر'} از بازه‌ی قبل`;
    },
    deltaTone(current, before) { return !before || current === before ? 'muted' : current > before ? 'good' : 'bad'; },
    presetRange(key) { return { from: shift(this.today, 1 - Number(key)), to: this.today }; },
    readQuery() {
      const q = this.$route.query;
      if (ISO.test(q.from || '') && ISO.test(q.to || '') && q.from <= q.to) { this.preset = 'custom'; this.range = { from: q.from, to: q.to }; }
      else { this.preset = PRESETS.includes(q.range) ? q.range : '30'; this.range = this.presetRange(this.preset); }
    },
    writeQuery() {
      const query = this.preset === 'custom' ? { from: this.range.from, to: this.range.to } : { range: this.preset };
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }, () => {}, () => {}); // vue-router 2: callbacks, no promise
    },
    setPreset(key) { this.preset = key; this.range = this.presetRange(key); this.previousRange = null; this.load(); },
    openCustom() { this.customRange = [this.range.from, this.range.to]; this.customOpen = true; },
    applyCustom(value) {
      const list = (Array.isArray(value) ? value : this.customRange).map(item => (item && item.format ? item.format('YYYY-MM-DD') : String(item || '').slice(0, 10)));
      const [from, to] = list;
      if (!ISO.test(from || '') || !ISO.test(to || '')) return;
      this.preset = 'custom'; this.range = from <= to ? { from, to } : { from: to, to: from }; this.previousRange = null; this.customOpen = false; this.load();
    },
    // A click on the chart narrows everything to that day or week; one step back is kept.
    pickPoint(point) {
      if (point.from === this.range.from && point.to === this.range.to) return;
      this.previousRange = { preset: this.preset, range: this.range };
      this.preset = 'custom'; this.range = { from: point.from, to: point.to }; this.load();
    },
    restoreRange() { this.preset = this.previousRange.preset; this.range = this.previousRange.range; this.previousRange = null; this.load(); },
    async load() {
      if (!this.project) return;
      this.writeQuery();
      const ticket = ++this.sequence;
      this.loading = true; this.error = '';
      try {
        const res = await this.$ApiServiceLayer.get(`/core/api/admin/dashboard/?p=${this.project}&from=${this.range.from}&to=${this.range.to}`);
        if (ticket !== this.sequence) return;
        if (res.status === 403) { this.denied = true; return; }
        if (res.status !== 200) { this.error = 'داده‌های داشبورد دریافت نشد.'; return; }
        this.denied = false;
        this.data = res.data;
        this.loaded = true;
        this.updatedAt = new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
      } catch (_) {
        if (ticket === this.sequence) this.error = 'ارتباط با سرور برقرار نشد.';
      } finally {
        if (ticket === this.sequence) this.loading = false;
      }
    },
    // A project can still point its dashboard menu at an external report; it stays one tab away.
    async loadEmbed() {
      try {
        const res = await this.$ApiServiceLayer.get(
          this.$PATH.RELATIVE_PATH.GET.MENU_LIST + '?p=' + this.project + '&is_active=true&has_iframe=true&frontend_route_url=/dashboard',
          this.$PATH.SERVICE_NAME.AUTH,
        );
        this.iframeLink = res.status === 200 && res.data.length ? res.data[0].iframe_link : null;
      } catch (_) { this.iframeLink = null; }
      if (!this.iframeLink) this.view = 'kpi';
    },
  },
};
</script>

<style scoped>
.dashboard { display: grid; gap: 16px; color: var(--text); }
.dash-head { position: relative; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 16px 18px 19px;
  border-radius: var(--r-md); background: var(--navy-900); color: #fff; overflow: hidden; }
.head-bars { position: absolute; inset-inline: 0; bottom: 0; direction: ltr; }
.head-copy { display: grid; gap: 2px; min-width: 0; }
.head-copy .vn-kicker { color: var(--shell-text-2); }
.dash-head h1 { color: #fff; font-size: 18px; line-height: 1.6; margin: 0; font-weight: 900; }
.dash-head p { color: var(--shell-text-3); font-size: 12px; margin: 0; }
.head-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.ghost-btn { display: inline-flex; align-items: center; gap: 7px; min-height: 38px; padding: 6px 14px; border-radius: var(--r-sm); border: 1px solid var(--shell-line);
  background: var(--navy-800); color: #fff; font: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; white-space: nowrap; --icon-accent: var(--amber-500); }
.ghost-btn:hover:not(:disabled) { background: var(--navy-700); }
.ghost-btn:disabled { opacity: .6; cursor: default; }
.filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 12px; }
.segmented { position: relative; display: inline-flex; flex-wrap: wrap; gap: 3px; padding: 3px; border-radius: var(--r-sm); background: var(--surface); border: 1px solid var(--line); }
.segmented button { min-height: 34px; padding: 5px 14px; border: 0; border-radius: var(--r-xs); background: transparent; color: var(--text-2); font: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.segmented button:hover { color: var(--text); background: var(--navy-50); }
.segmented button.active { background: var(--navy-900); color: #fff; }
.segmented.on-dark { background: var(--navy-850); border-color: var(--shell-line); }
.segmented.on-dark button { color: var(--shell-text-2); }
.segmented.on-dark button:hover { background: var(--navy-800); color: #fff; }
.segmented.on-dark button.active { background: var(--teal-500); color: var(--navy-900); }
.range-anchor { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; bottom: 0; left: 0; }
.chip { display: inline-flex; align-items: center; gap: 8px; min-height: 32px; padding: 3px 12px; border-radius: 99px; border: 1px solid var(--teal-300); background: var(--teal-50);
  color: var(--teal-700); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.error { padding: 11px 14px; border-radius: var(--r-sm); background: var(--danger-bg); color: var(--danger); font-size: 13px; font-weight: 700; }
.error button { border: 0; background: transparent; color: inherit; font: inherit; text-decoration: underline; cursor: pointer; }
.dash-body { display: grid; gap: 16px; transition: opacity .2s; }
.dash-body.refreshing { opacity: .55; }
.kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.skeleton { min-height: 118px; border-radius: var(--r-md); border: 1px solid var(--line);
  background: linear-gradient(90deg, var(--navy-50), var(--surface), var(--navy-50)); background-size: 200% 100%; animation: shimmer 1.4s linear infinite; }
@keyframes shimmer { to { background-position: -200% 0; } }
.grid { display: grid; gap: 16px; }
.two-one { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
.halves { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.thirds { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.panel { min-width: 0; padding: 18px; border-radius: var(--r-md); background: var(--surface); border: 1px solid var(--line); }
.panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.panel-head h2 { font-size: 15px; font-weight: 900; color: var(--navy-900); margin: 0 0 3px; }
.panel-head p { font-size: 11.5px; color: var(--text-3); margin: 0; line-height: 1.7; }
.state-panel { text-align: center; }
.service-grid { display: grid; gap: 10px; }
.service-tile { display: grid; gap: 2px; padding: 12px 14px; border-radius: var(--r-sm); border: 1px solid var(--line); background: var(--surface-sunken); color: var(--text-2); text-decoration: none; font-size: 12px; }
a.service-tile:hover { border-color: var(--navy-900); text-decoration: none; color: var(--text); }
.service-tile b { font-size: 24px; color: var(--navy-900); font-weight: 900; }
.service-tile b.alert { color: var(--pink-700); }
.service-tile small { font-size: 11px; color: var(--text-3); }
.embed-frame { position: relative; height: calc(100vh - 260px); min-height: 480px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-md); overflow: hidden; }
.embed-frame iframe { display: block; width: 100%; height: 100%; border: 0; }
.dashboard button:focus-visible, .dashboard a:focus-visible { outline: 3px solid var(--focus); outline-offset: 2px; }
@media (max-width: 1180px) { .kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } .two-one, .thirds { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .halves { grid-template-columns: 1fr; } }
@media (max-width: 420px) { .kpis { grid-template-columns: 1fr; } }
</style>
